import { useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import type { HubData } from './useHubData'
import type { IdentifiedProperty } from '../components/identification/IdentificationPropertyRow'

const DEMO1_EMAIL = 'demo1@open1031.ai'

type SaveData = (data: Partial<HubData>) => void
type GetData = <T extends keyof HubData>(key: T) => HubData[T] | undefined

export function useDemoAcquisitionPrefill(
  getData: GetData,
  saveData: SaveData,
  saveInitialData?: SaveData,
  getExchangeData?: GetData,
  getInitialData?: GetData
) {
  const { user } = useAuth()
  const prefillAttemptedRef = useRef(false)

  useEffect(() => {
    if (prefillAttemptedRef.current) return
    if (!user?.email || String(user.email).toLowerCase().trim() !== DEMO1_EMAIL) return

    const existingSeller = getData('seller')
    const existingReplacement = getData('replacement')
    if (existingSeller?.names || existingReplacement?.addressOrLegalDescription) return

    prefillAttemptedRef.current = true

    import('../lib/demoInit').then(({ getDemoAcquisitionPrefill }) => {
      const prefill = getDemoAcquisitionPrefill()

      const canonicalPhaseOne = getInitialData?.('exchangerPhaseOne') as HubData['exchangerPhaseOne'] | undefined
      if (canonicalPhaseOne?.names) {
        prefill.exchangerPhaseTwo = {
          names: canonicalPhaseOne.names,
          homeAddress: canonicalPhaseOne.homeAddress,
          taxIdNumber: canonicalPhaseOne.taxIdNumber,
        }
      }

      const rawIdentifiedProperties = getExchangeData?.('identificationProperties') as IdentifiedProperty[] | undefined
      const primaryIdentifiedProperty = Array.isArray(rawIdentifiedProperties) && rawIdentifiedProperties.length > 0
        ? rawIdentifiedProperties[0]
        : null

      if (primaryIdentifiedProperty) {
        const derivedAddress = primaryIdentifiedProperty.dstTitle
          ? `${primaryIdentifiedProperty.address}`
          : primaryIdentifiedProperty.address
        prefill.replacement = {
          ...prefill.replacement,
          addressOrLegalDescription: derivedAddress,
        }
      }

      saveData(prefill)
      if (saveInitialData) {
        saveInitialData({
          exchangerPhaseTwo: prefill.exchangerPhaseTwo,
          seller: prefill.seller,
          closingCompanyPurchase: prefill.closingCompanyPurchase,
          replacement: prefill.replacement,
        })
      }
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    })
  }, [user?.email, getData, saveData, saveInitialData, getExchangeData, getInitialData])
}
